"use client";

import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { Image } from "react-bootstrap";

export default function BrandsSliderSection(props) {
  const [imagesRow1, setImagesRow1] = useState([]);
  const [imagesRow2, setImagesRow2] = useState([]);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);

  // Fallback brand image arrays
  const fallbackRow1 = [
    "/assets/img/codeqlik_partner1.png",
    "/assets/img/codeqlik_partner10.PNG",
    "/assets/img/codeqlik_partner11.png",
    "/assets/img/codeqlik_partner12-removebg-preview.png",
    "/assets/img/codeqlik_partner2.png",
    "/assets/img/codeqlik_partner3.png",
    "/assets/img/codeqlik_partner4.png",
    "/assets/img/codeqlik_partner5-removebg-preview.png",
    "/assets/img/codeqlik_partner6.png",
  ];

  const fallbackRow2 = [
    "/assets/img/codeqlik_partner7.jpeg",
    "/assets/img/codeqlik_partner8-removebg-preview.png",
    "/assets/img/logo-14.jpeg",
    "/assets/img/logo2.jpeg",
    "/assets/img/logo-3.jpeg",
    "/assets/img/logo-5.jpeg",
    "/assets/img/logo-4.jpeg",
    "/assets/img/logo13.jpeg",
    "/assets/img/codeqlik_partner1.png",
  ];

  useEffect(() => {
    const apiUrl = (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || '';

    const formatLogos = (rawLogos) => {
      if (!Array.isArray(rawLogos)) return [];
      return rawLogos.map(logo => {
        const logoStr = typeof logo === 'string' ? logo : (logo?.url || '');
        if (!logoStr) return '';
        
        let finalUrl = logoStr;
        if (logoStr.startsWith('http') && !logoStr.includes('/uploads/')) {
          finalUrl = logoStr;
        } else {
          let cleanPath = logoStr;
          if (logoStr.includes('/uploads/')) {
            cleanPath = logoStr.substring(logoStr.indexOf('/uploads/'));
          }
          
          const path = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
          
          if (path.startsWith('/uploads/')) {
            finalUrl = `${apiUrl}${path}`;
          } else {
            finalUrl = path;
          }
        }

        // Apply Cloudinary optimization and scaling for logos (mobile optimized)
        if (finalUrl.includes("res.cloudinary.com") && finalUrl.includes("/image/upload/")) {
          if (!finalUrl.includes("f_auto") && !finalUrl.includes("q_auto")) {
            finalUrl = finalUrl.replace("/image/upload/", "/image/upload/f_auto,q_auto:eco,w_100,c_fit/");
          }
        }
        
        return finalUrl;
      }).filter(Boolean);
    };

    // If dynamicData is provided, use it
    if (props.dynamicData && props.dynamicData.logos && props.dynamicData.logos.length > 0) {
      const allLogos = formatLogos(props.dynamicData.logos);
      const half = Math.ceil(allLogos.length / 2);
      setImagesRow1(allLogos.slice(0, half));
      setImagesRow2(allLogos.slice(half));
      return;
    }

    const fetchBrands = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/brand/public?isActive=true&limit=100`);
        const data = await response.json();

        if (data.success && data.data && data.data.brands && data.data.brands.length > 0) {
          const allLogos = data.data.brands.reduce((acc, brand) => {
            const rawLogos = brand.images || brand.logos || [];
            const formattedLogos = formatLogos(rawLogos);
            return [...acc, ...formattedLogos];
          }, []);

          if (allLogos.length > 0) {
            // Split logos into two rows
            const half = Math.ceil(allLogos.length / 2);
            setImagesRow1(allLogos.slice(0, half));
            setImagesRow2(allLogos.slice(half));
          } else {
            setImagesRow1(fallbackRow1);
            setImagesRow2(fallbackRow2);
          }
        } else {
          setImagesRow1(fallbackRow1);
          setImagesRow2(fallbackRow2);
        }
      } catch (error) {
        console.error("Error fetching brands:", error);
        setImagesRow1(fallbackRow1);
        setImagesRow2(fallbackRow2);
      }
    };

    fetchBrands();
  }, [props.dynamicData]);

  useEffect(() => {
    if (imagesRow1.length === 0) return;

    const startInfiniteScroll = (ref, speed, direction, pause = 1) => {
      if (!ref.current) return;
      const inner = ref.current.querySelector(".slider-inner");
      if (!inner) return;

      // Clear existing clones to avoid duplicates on re-renders
      const clones = inner.querySelectorAll(".clone");
      clones.forEach(clone => clone.remove());

      // Clone slides to create infinite loop
      const slides = Array.from(inner.children);
      slides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.classList.add("clone");
        inner.appendChild(clone);
      });

      let totalWidth = inner.scrollWidth / 2;

      gsap.killTweensOf(inner);
      gsap.set(inner, { x: direction === "left" ? "0%" : `-${totalWidth}px` });
      gsap.to(inner, {
        x: direction === "left" ? `-${totalWidth}px` : "0%",
        duration: speed,
        ease: "linear",
        repeat: -1,
        repeatDelay: pause,
      });
    };

    // Small delay to ensure DOM is updated with new images before starting GSAP
    const timer = setTimeout(() => {
      startInfiniteScroll(row1Ref, 30, "left", 0);
      startInfiniteScroll(row2Ref, 30, "right", 0);
    }, 100);

    return () => clearTimeout(timer);
  }, [imagesRow1, imagesRow2]);

  // Inline styles
  const containerStyle = {
    overflow: "hidden",
    width: "100%",
    paddingTop: 8,
    paddingBottom: 8,
    WebkitMaskImage:
      "linear-gradient(90deg, rgba(0,0,0,0) 0, rgba(0,0,0,1) 48px, rgba(0,0,0,1) calc(100% - 48px), rgba(0,0,0,0) 100%)",
    maskImage:
      "linear-gradient(90deg, rgba(0,0,0,0) 0, rgba(0,0,0,1) 48px, rgba(0,0,0,1) calc(100% - 48px), rgba(0,0,0,0) 100%)",
  };

  const rowStyle = {
    overflow: "hidden",
    position: "relative",
    width: "100%",
  };

  const innerStyle = {
    display: "flex",
    gap: 32,
    alignItems: "center",
    willChange: "transform",
  };

  const cardStyle = {
    padding: "6px 16px",
    background: "#fff",
    border: "1px solid #e9ecef",
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 160,
    height: 52,
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  };

  const imgStyle = {
    height: 48,
    width: "auto",
    objectFit: "contain",
    display: "block",
  };

  return (
    <section className="py-3">
      <div className="container-fluid px-0" style={{ overflow: "hidden" }}>
        {/* Heading Section */}
        <div className="text-center mb-3" data-reveal="fade">
          <span className="brands text-[#F48428]">
            {props.dynamicData?.title || "Brands Collaboration"}
          </span>
          <h3 className="mt-2 brand-trust text-white">
            Trusted by Leading Brands
          </h3>
        </div>

        {/* Brand Slider Section */}
        <div data-reveal="slide-right">
          <div className="slider-container-home" style={containerStyle}>
            {/* Row 1 */}
            <div className="slider-row slider-row-left" ref={row1Ref} style={rowStyle}>
              <div className="slider-inner" style={innerStyle}>
                {imagesRow1.map((src, index) => (
                  <div className="slider-card" style={cardStyle} key={`r1-${index}`}>
                    <Image src={src} alt={`brand-${index}`} style={imgStyle} />
                  </div>
                ))}
              </div>
            </div>

            {/* Row 2 */}
            <div className="slider-row slider-row-right mt-2" ref={row2Ref} style={rowStyle}>
              <div className="slider-inner" style={innerStyle}>
                {imagesRow2.map((src, index) => (
                  <div className="slider-card" style={cardStyle} key={`r2-${index}`}>
                    <Image src={src} alt={`brand-${index}`} style={imgStyle} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
